/** Deserializer to convert delta to json 
 *  Sample Input {
      ops: [
        {
          insert: "my"
        },
        {
          insert: "name",
          attributes: {
            bold: true
          }
        },
        {
          insert: "is"
        },
        {
          insert: "vivek",
          attributes: {
            bold: true,
            italic: true
          }
        }
      ]
    }
    
    Sample Output [{
      "type": "paragraph",
      "nodes": [
        {
          "type": "text",
          "text": "my"
        },
        {
          "type": "bold",
          "nodes": [
            {
              "type": "text",
              "text": "name"
            }
          ]
        },
        {
          "type": "text",
          "text": "is"
        },
        {
          "type": "bold",
          "nodes": [
            {
              "type": "italics",
              "nodes": [
                {
                  "type": "text",
                  "text": "vivek"
                }
              ]
            }
          ]
        }
      ]
    }]
*/
import Quill from "quill";

import { cloneDeep } from "base/utils";

import {
  FORMAT_TYPES,
  ASSIST_FORMAT_TYPES,
  CUSTOM_BLOCK_TYPES
} from "../index.js";

const Delta = Quill.import("delta");
const ATTRIBUTE_ORDER = [
  FORMAT_TYPES.LINK,
  FORMAT_TYPES.COLOR,
  FORMAT_TYPES.STRIKE,
  FORMAT_TYPES.UNDERLINE,
  FORMAT_TYPES.ITALIC,
  FORMAT_TYPES.BOLD,
  FORMAT_TYPES.SUBSCRIPT,
  FORMAT_TYPES.SUPSCRIPT
];

function sortAttributes(attributes) {
  let sortedAttribute = {};
  ATTRIBUTE_ORDER.forEach((item) => {
    if (attributes[item]) sortedAttribute[item] = attributes[item];
  });
  return sortedAttribute;
}

function getMarkNodes(attributes, childNodes, text) {
  let tempattributes = Object.assign({}, attributes);
  if (Object.keys(attributes).length > 0) {
    let mark = Object.keys(attributes)[0];
    if (attributes[mark]) {
      let obj = {};
      obj.type = mark;
      obj.data = {};
      if (mark === FORMAT_TYPES.LINK) {
        obj.data = attributes[mark];
      } else if (mark === FORMAT_TYPES.COLOR) {
        obj.data = {
          [FORMAT_TYPES.COLOR]: attributes[mark]
        };
      }
      obj.nodes = [];
      Reflect.deleteProperty(tempattributes, mark);
      getMarkNodes(tempattributes, obj.nodes, text);
      childNodes.push(obj);
    }
  } else {
    let obj = {};
    obj.type = "text";
    obj.text = text || "";
    childNodes = childNodes || [];
    childNodes.push(obj);
  }
}

function getParagraphNodes(item, nodes) {
  if (!item.attributes) {
    nodes.push({
      type: "text",
      text: item.insert || ""
    });
  } else {
    let childNodes = [];
    let sortedAttributes = sortAttributes(item.attributes);
    getMarkNodes(sortedAttributes, childNodes, item.insert);
    nodes.push(childNodes[0]);
  }
}

function getObject(item, nodes) {
  let obj = {};
  let type = Object.keys(item.insert).filter(
    (item) => item !== "attributes"
  )[0];
  obj.type = type;
  obj.data = item.insert[type] !== true ? item.insert[type] : {};
  obj.nodes = [];
  if (!item.attributes) {
    if (!(type === FORMAT_TYPES.IMAGE && item.insert?.image?.loading)) {
      obj.nodes.push({
        type: "text",
        text: (item.insert && item.insert.text) || ""
      });
      nodes.push(obj);
    }
  } else {
    let childNodes = [];
    let sortedAttributes = sortAttributes(item.attributes);
    getMarkNodes(sortedAttributes, childNodes);
    obj.nodes = childNodes;
    // customObj.nodes = customObj.nodes[0];
    nodes.push(obj);
  }
}

const deserializerObject = (ops) => {
  let nodes = [];
  ops.forEach((item) => {
    if (typeof item.insert === "object") {
      getObject(item, nodes);
    } else {
      getParagraphNodes(item, nodes);
    }
  });
  return nodes;
};

const CHANGE_TYPE = {
  [FORMAT_TYPES.CODE]: "code_line",
  [FORMAT_TYPES.UL_LIST]: "list_item",
  [FORMAT_TYPES.OL_LIST]: "list_item",
  [FORMAT_TYPES.BLOCKQUOTE]: FORMAT_TYPES.BLOCKQUOTE
};

/** function to wrap multi line block to single container block */
function postProcessing(nodeJSON) {
  const BLOCKTYPES = [
    FORMAT_TYPES.UL_LIST,
    FORMAT_TYPES.OL_LIST,
    FORMAT_TYPES.CODE
  ];

  Object.keys(CHANGE_TYPE).forEach((key) => {
    let indexes = nodeJSON
      .map((e, i) => (e.type === key ? i : ""))
      .filter(String);
    let newPositions = [
      {
        startIndex: indexes[0],
        length: 1
      }
    ];

    if (indexes.length === 0) {
      return;
    }

    /** To find all indexes for the block types from the nodeJSON*/
    let i = 0;
    indexes.forEach((value, index) => {
      if (value + 1 === indexes[index + 1]) {
        newPositions[i].length = newPositions[i].length + 1;
      } else if (indexes[index + 1] - value !== 1) {
        newPositions.push({
          startIndex: indexes[index + 1] || indexes[index],
          length: 1
        });
        i = i + 1;
      }
    });
    newPositions.pop();

    /** Wrapping multiline blocks to a single block type using indexes */
    newPositions.forEach((item, index) => {
      const startIndex = item.startIndex;
      const endIndex = item.startIndex + item.length;
      /** Saving a reference of nodeJSON */
      let tempArray = cloneDeep(nodeJSON);
      nodeJSON.splice(startIndex, item.length);

      /** Since for blockquote type we dont have a subtype wrapping all paragraph blocks to a single block */
      let blockQuoteBlock = false;
      let newData = {};
      if (key === FORMAT_TYPES.BLOCKQUOTE) {
        blockQuoteBlock = [];

        tempArray.slice(startIndex, endIndex).forEach((item) => {
          if (item.data.align) {
            item.nodes[0].data["align"] = item.data.align;
          }
          blockQuoteBlock.push(item.nodes[0]);
        });
      }

      /** web support for the list types */
      if ([FORMAT_TYPES.UL_LIST, FORMAT_TYPES.OL_LIST].includes(key)) {
        newData.listStyle =
          key === FORMAT_TYPES.UL_LIST ? "initial" : "decimal";
      }

      /** changing type of list, code for single block */
      tempArray.slice(startIndex, endIndex).forEach((item) => {
        if (BLOCKTYPES.includes(item.type)) {
          item.type = CHANGE_TYPE[key];
        }
      });

      nodeJSON.splice(startIndex, 0, {
        type: key,
        data: newData,
        nodes: blockQuoteBlock
          ? [...blockQuoteBlock]
          : [...tempArray.slice(startIndex, endIndex)]
      });

      /** to mutate next startIndex */
      if (newPositions[index + 1]) {
        let count = 0;
        let slicedArray = newPositions.slice(0, index + 1);
        slicedArray.forEach((item) => {
          count += item.length;
        });
        if (count) {
          newPositions[index + 1].startIndex =
            newPositions[index + 1].startIndex - (count - slicedArray.length);
        }
      }
    });
  });
}

function capitalize(value) {
  return value.substring(0, 1).toUpperCase() + value.substring(1);
}

export function deserializer(ops) {
  let delta = new Delta(ops);
  let nodeJSON = [];
  delta.eachLine((line, blockAttributes) => {
    if (line.ops.length === 0 && Object.keys(blockAttributes).length === 0) {
      nodeJSON.push({
        type: "paragraph",
        data: {},
        nodes: [
          {
            type: "text",
            text: ""
          }
        ]
      });
    } else if (Object.keys(blockAttributes).length > 0) {
      let nodes = deserializerObject(line.ops);
      let blockType = "paragraph";
      let data = {};
      if (blockAttributes[FORMAT_TYPES.HEADER]) {
        blockType = ASSIST_FORMAT_TYPES[blockAttributes.header];
      } else if (blockAttributes[FORMAT_TYPES.BLOCKQUOTE]) {
        blockType = FORMAT_TYPES.BLOCKQUOTE;
      } else if (
        blockAttributes.list === ASSIST_FORMAT_TYPES[FORMAT_TYPES.UL_LIST]
      ) {
        blockType = FORMAT_TYPES.UL_LIST;
      } else if (
        blockAttributes.list === ASSIST_FORMAT_TYPES[FORMAT_TYPES.OL_LIST]
      ) {
        blockType = FORMAT_TYPES.OL_LIST;
      } else if (blockAttributes[FORMAT_TYPES.CODE]) {
        blockType = FORMAT_TYPES.CODE;
      }
      if (line.ops.length === 0) {
        nodes = [
          {
            type: "text",
            text: ""
          }
        ];
      } else if (
        [
          FORMAT_TYPES.UL_LIST,
          FORMAT_TYPES.OL_LIST,
          FORMAT_TYPES.BLOCKQUOTE
        ].includes(blockType)
      ) {
        const currNode = {
          type: "paragraph",
          nodes,
          data: {}
        };
        if (blockAttributes.align) {
          currNode["data"] = { align: capitalize(blockAttributes.align) };
        }
        nodes = [currNode];
      } else if (blockAttributes.align) {
        nodes = deserializerObject(line.ops) || [];
        data.align = capitalize(blockAttributes.align);
      }
      let obj = {
        type: blockType,
        data,
        nodes
      };
      nodeJSON.push(obj);
    } else {
      let arr = line.ops;
      let isCustomEmbed = arr.filter((item) =>
        CUSTOM_BLOCK_TYPES.includes(Object.keys(item.insert)[0])
      );
      if (isCustomEmbed.length > 0) {
        let textNodes = [];
        arr.forEach((item) => {
          if (CUSTOM_BLOCK_TYPES.includes(Object.keys(item.insert)[0])) {
            let nodes = deserializerObject([item]);
            if (nodes.length > 0) {
              nodeJSON.push(nodes[0]);
            }
          } else {
            textNodes.push(item);
          }
        });
        if (textNodes?.length > 0) {
          let obj = {
            type: "paragraph",
            data: {},
            nodes: deserializerObject(textNodes)
          };
          nodeJSON.push(obj);
        }
      } else {
        let obj = {
          type: "paragraph",
          data: {},
          nodes: deserializerObject(line.ops) || []
        };
        nodeJSON.push(obj);
      }
    }
  });
  postProcessing(nodeJSON);
  return nodeJSON;
}
